import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
    BarChart3, Users, Sparkles, CreditCard, TrendingUp, Activity,
    Loader2, ArrowLeft,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const RANGES = [
    { days: 7, label: "7 days" },
    { days: 30, label: "30 days" },
    { days: 90, label: "90 days" },
];

export default function AdminAnalytics() {
    const { user } = useAuth();
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.role !== "admin") return;
        setLoading(true);
        api.get(`/admin/analytics?days=${days}`)
            .then(({ data }) => setData(data))
            .catch(() => toast.error("Could not load analytics"))
            .finally(() => setLoading(false));
    }, [days, user?.role]);

    if (user && user.role !== "admin") {
        return <Navigate to="/app" replace />;
    }

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="admin-analytics-page">
            <Link
                to="/app/profile"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                data-testid="analytics-back"
            >
                <ArrowLeft size={14} /> Back to profile
            </Link>
            <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow">Admin</div>
                    <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">
                        Analytics
                    </h1>
                </div>
                <div className="flex gap-1" data-testid="analytics-range">
                    {RANGES.map((r) => (
                        <button
                            key={r.days}
                            type="button"
                            onClick={() => setDays(r.days)}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                days === r.days
                                    ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                    : "border-[#0D1B3E]/20 text-[#0D1B3E]/70 hover:border-[#C9A84C]"
                            }`}
                            data-testid={`analytics-range-${r.days}`}
                        >
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading || !data ? (
                <div className="mt-16 flex items-center gap-3 text-[#0D1B3E]/60">
                    <Loader2 size={18} className="animate-spin" /> Loading analytics…
                </div>
            ) : (
                <>
                    <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="analytics-kpis">
                        <Kpi icon={Users} label="Total users" value={data.totals.users}
                            sub={`${data.counters.signups} new in period`} />
                        <Kpi icon={Sparkles} label="Trial → paid conversion"
                            value={`${data.totals.conversion_pct}%`}
                            sub={`${data.totals.paid_users} paid / ${data.totals.trials_total} trials`} />
                        <Kpi icon={CreditCard} label="MRR (indicative)"
                            value={`€${data.totals.mrr_eur.toLocaleString()}`}
                            sub={Object.entries(data.totals.active_subs_by_plan)
                                .map(([k, n]) => `${n} ${k}`).join(" · ") || "no active subs"} />
                        <Kpi icon={TrendingUp} label="Queries in period"
                            value={data.counters.queries}
                            sub={`${data.counters.uploads} uploads`} />
                    </div>

                    <div className="mt-8 grid gap-6 lg:grid-cols-2">
                        <ChartCard title="Signups per day" data={data.signups_by_day} testId="analytics-signups-chart" />
                        <ChartCard title="Queries per day" data={data.queries_by_day} testId="analytics-queries-chart" />
                    </div>

                    <div className="mt-8 grid gap-6 lg:grid-cols-2">
                        <div className="card-flat p-6" data-testid="analytics-modules">
                            <div className="label-eyebrow">Most-used modules</div>
                            <div className="mt-4 space-y-2.5">
                                {data.modules.length === 0 && (
                                    <div className="text-sm text-[#0D1B3E]/55">No query events in this period.</div>
                                )}
                                {data.modules.map((m) => {
                                    const max = data.modules[0]?.count || 1;
                                    return (
                                        <div key={m.module} className="flex items-center gap-3">
                                            <span className="w-32 shrink-0 text-xs text-[#0D1B3E]/70 capitalize truncate">
                                                {m.module.replaceAll("_", " ")}
                                            </span>
                                            <div className="flex-1 h-4 bg-[#0D1B3E]/5">
                                                <div className="h-4 bg-[#C9A84C]"
                                                    style={{ width: `${Math.max(4, (m.count / max) * 100)}%` }} />
                                            </div>
                                            <span className="text-xs font-medium w-8 text-right">{m.count}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="card-flat p-6" data-testid="analytics-funnel">
                            <div className="label-eyebrow">Lifecycle funnel (period)</div>
                            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                                <FunnelRow label="Signups" value={data.counters.signups} />
                                <FunnelRow label="Trial starts" value={data.counters.trial_starts} />
                                <FunnelRow label="Trial expiries" value={data.counters.trial_expiries} />
                                <FunnelRow label="Checkouts started" value={data.counters.checkouts_started} />
                                <FunnelRow label="Payments succeeded" value={data.counters.payments_succeeded} />
                                <FunnelRow label="Payments failed" value={data.counters.payments_failed} />
                                <FunnelRow label="Upgrades" value={data.counters.upgrades} />
                                <FunnelRow label="Cancellations" value={data.counters.cancellations} />
                                <FunnelRow label="Downgrades/churn" value={data.counters.downgrades} />
                                <FunnelRow label="Account deletions" value={data.counters.account_deletions} />
                                <FunnelRow label="Logins" value={data.counters.logins} />
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 card-flat p-6" data-testid="analytics-events-feed">
                        <div className="flex items-center gap-2">
                            <Activity size={15} color="#C9A84C" />
                            <span className="label-eyebrow">Recent events</span>
                        </div>
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left border-b border-[#0D1B3E]/10">
                                        <th className="py-2 pr-4 label-eyebrow">When</th>
                                        <th className="py-2 pr-4 label-eyebrow">Event</th>
                                        <th className="py-2 pr-4 label-eyebrow">User</th>
                                        <th className="py-2 label-eyebrow">Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.recent_events.map((e, i) => (
                                        <tr key={i} className="border-b border-[#0D1B3E]/5">
                                            <td className="py-2 pr-4 whitespace-nowrap text-[#0D1B3E]/60">
                                                {new Date(e.at).toLocaleString()}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <span className="px-2 py-0.5 bg-[#0D1B3E]/5 uppercase tracking-[0.14em] text-[10px]">
                                                    {e.kind}
                                                </span>
                                            </td>
                                            <td className="py-2 pr-4 text-[#0D1B3E]/75">{e.email || "–"}</td>
                                            <td className="py-2 text-[#0D1B3E]/60 max-w-[280px] truncate">
                                                {Object.entries(e.meta || {})
                                                    .map(([k, v]) => `${k}: ${v}`).join(" · ") || "–"}
                                            </td>
                                        </tr>
                                    ))}
                                    {data.recent_events.length === 0 && (
                                        <tr><td colSpan={4} className="py-6 text-center text-[#0D1B3E]/55">No events yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub }) {
    return (
        <div className="card-flat p-5">
            <div className="flex items-center gap-2 text-[#0D1B3E]/60">
                <Icon size={14} color="#C9A84C" />
                <span className="text-[10px] uppercase tracking-[0.2em]">{label}</span>
            </div>
            <div className="font-display text-3xl mt-2 tracking-tight">{value}</div>
            <div className="text-xs text-[#0D1B3E]/55 mt-1">{sub}</div>
        </div>
    );
}

function FunnelRow({ label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-[#0D1B3E]/5 py-1.5">
            <span className="text-[#0D1B3E]/65">{label}</span>
            <span className="font-medium">{value}</span>
        </div>
    );
}

function ChartCard({ title, data, testId }) {
    const max = Math.max(1, ...data.map((d) => d.count));
    return (
        <div className="card-flat p-6" data-testid={testId}>
            <div className="flex items-center gap-2">
                <BarChart3 size={15} color="#C9A84C" />
                <span className="label-eyebrow">{title}</span>
            </div>
            {data.length === 0 ? (
                <div className="mt-6 text-sm text-[#0D1B3E]/55">No data in this period.</div>
            ) : (
                <div className="mt-5 flex items-end gap-1 h-32">
                    {data.map((d) => (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${d.date}: ${d.count}`}>
                            <span className="text-[9px] text-[#0D1B3E]/55">{d.count}</span>
                            <div className="w-full bg-[#0D1B3E]"
                                style={{ height: `${Math.max(4, (d.count / max) * 100)}px` }} />
                            <span className="text-[8px] text-[#0D1B3E]/45 truncate w-full text-center">
                                {d.date.slice(5)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
